/**
 * Hashes: which page, which conversation, and the way back out.
 *
 * Nothing here knows what is *in* IP 设置. It used to: a table of five entry
 * points, nineteen rewritten titles and thirty namespaces in eight groups, all
 * hand-written. That moved to the plugins' own
 * declarations and `connections/directory.ts`, which projects them; this file
 * is left with URL shapes, which are the shell's own.
 */

import { API_BASE } from "../lib/base";
import { readDraft } from "../lib/drafts";

export function chatHref(sessionId: string): string {
  return `#/chat?${new URLSearchParams({ session: sessionId })}`;
}

/** Where the Agent's work shows: the way back from a page that came from a conversation the owner cannot read. */
export const WORKSPACE_HREF = "#/today";

/**
 * Where a way back "to the conversation" leads. `drawn` is whether the console draws it (`useConversationDrawn`):
 * a conversation the Agent holds for itself is empty to the owner, so the way back goes to the workspace instead.
 */
export function conversationHref(sessionId: string, drawn?: boolean): string {
  return drawn === false ? WORKSPACE_HREF : chatHref(sessionId);
}

export function approvalHref(sessionId?: string): string {
  return "#/approvals" + (sessionId ? `?${new URLSearchParams({ session: sessionId })}` : "");
}

export function rememberedWorkHref(scope?: string): string | undefined {
  const saved = readDraft<unknown>("navigation:work", null, scope);
  return localWorkHref(saved);
}

export function localWorkHref(value: unknown): string | undefined {
  return typeof value === "string" && /^#\/(?!\/)/.test(value) && !/[\\\r\n]/.test(value) ? value : undefined;
}

/** Settings links carry the original work, not another settings return stack. */
export function settingsWorkHref(value: unknown): string | undefined {
  let route = localWorkHref(value);
  while (route) {
    const [path, query] = route.split("?");
    const params = new URLSearchParams(query);
    const settings = path === "#/connections" || path === "#/settings";
    const parent = settings ? params.get("return") : params.get("from_settings") ? params.get("source_settings_return") : null;
    if (!parent) return route;
    route = localWorkHref(parent);
  }
  return undefined;
}

export function connectionReturnHref(hash: string, sessionId?: string, scope?: string, drawn?: boolean): string | undefined {
  const params = new URLSearchParams(hash.split("?")[1]);
  const explicit = settingsWorkHref(params.get("return"));
  // A way back written as the conversation, for one the owner cannot read, leads to the workspace instead.
  if (explicit) return sessionId && drawn === false && explicit === chatHref(sessionId) ? WORKSPACE_HREF : explicit;
  if (params.has("request_id")) return sessionId ? conversationHref(sessionId, drawn) : undefined;
  return rememberedWorkHref(scope) ?? (sessionId ? conversationHref(sessionId, drawn) : undefined);
}

/**
 * A settings destination, or IP 设置 with nothing selected.
 *
 * `namespace` is optional because the answer to 「which part of it」 is a
 * declaration the page reads after it loads, not something a caller can know.
 */
export function connectionHref(namespace?: string, sessionId?: string, returnTo = rememberedWorkHref()): string {
  returnTo = settingsWorkHref(returnTo);
  return `#/connections?${new URLSearchParams({ ...(namespace ? { namespace } : {}), ...(sessionId ? { session: sessionId } : {}), ...(localWorkHref(returnTo) ? { return: returnTo! } : {}) })}`;
}

export function connectionSession(hash: string, fallback: string): string {
  const params = new URLSearchParams(hash.split("?")[1]);
  return params.get("session") || params.get("session_id") || fallback;
}

/** 从嵌入的设置返回工作时，保留 Agent 路径，退出嵌入布局。 */
export function leaveSettings(hash: string): { href: string; target?: "_top" } {
  return new URLSearchParams(location.search).get("workspace") === "settings"
    ? { href: `${API_BASE}/${hash}`, target: "_top" } : { href: hash };
}
